<script setup lang="ts">
import { refDebounced } from '@vueuse/core';
import { useQueryClient } from '@tanstack/vue-query';
import { computed, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useEquipmentList } from '@/composables/equipment/useEquipmentList';
import { useDeviceTypes } from '@/composables/equipment/useEquipmentLookups';
import { useIsSystemAdmin } from '@/composables/useIsSystemAdmin';
import { EquipmentService } from '@/services/equipment.services';
import type { IDeviceGroup } from '@/types/equipment.types';
import { typeIcon } from '@/utils/equipmentIcons';
import EquipmentConfirmModal from './EquipmentConfirmModal.vue';
import EquipmentField from './EquipmentField.vue';
import EquipmentSelect from './EquipmentSelect.vue';
import EquipmentSiteSelect from './EquipmentSiteSelect.vue';
import EquipmentStoreSelect from './EquipmentStoreSelect.vue';
import EquipmentTabShell from './EquipmentTabShell.vue';
import GroupFormModal from './GroupFormModal.vue';

const { t } = useI18n({ useScope: 'global' });
const queryClient = useQueryClient();
const { isSystemAdmin } = useIsSystemAdmin();
const { typeLabel } = useDeviceTypes();

const storeId = ref('');
const siteId = ref('');
const sort = ref<'name' | 'createdAt'>('name');
const search = ref('');
const debouncedSearch = refDebounced(search, 300);

watch(storeId, () => (siteId.value = ''));

const sortOptions = computed(() => [
  { value: 'name' as const, label: t('equipment.sortName') },
  { value: 'createdAt' as const, label: t('equipment.sortRecent') },
]);
const filtersActive = computed(
  () => !!(search.value || storeId.value || siteId.value),
);
const resetFilters = () => {
  search.value = '';
  storeId.value = '';
  siteId.value = '';
};

const list = useEquipmentList<IDeviceGroup>(
  'groups',
  () => ({
    storeIds: storeId.value ? [storeId.value] : undefined,
    siteIds: siteId.value ? [siteId.value] : undefined,
    sortBy: sort.value,
    sortOrder: sort.value === 'name' ? 'ASC' : 'DESC',
  }),
  EquipmentService.searchGroups,
);

// La recherche de sous-groupes n'a pas de filtre texte côté serveur : elle s'applique à la page affichée.
const rows = computed(() => {
  const needle = debouncedSearch.value.trim().toLowerCase();
  return needle
    ? list.rows.value.filter((g) =>
        [g.name, g.master.label, ...g.members.map((m) => m.label)]
          .join(' ')
          .toLowerCase()
          .includes(needle),
      )
    : list.rows.value;
});

const creating = defineModel<boolean>('creating', { default: false });
const editing = ref<IDeviceGroup | null>(null);
const deleting = ref<IDeviceGroup | null>(null);
const formOpen = computed(() => creating.value || !!editing.value);
const closeForm = () => {
  creating.value = false;
  editing.value = null;
};
const onDeleted = async () => {
  deleting.value = null;
  await queryClient.invalidateQueries({ queryKey: ['equipment'] });
};
</script>

<template>
  <EquipmentTabShell
    v-model:page="list.page.value"
    v-model:page-size="list.pageSize.value"
    :count="rows.length"
    :total="list.total.value"
    :page-count="list.pageCount.value"
    :is-loading="list.isLoading.value"
    :is-error="list.isError.value"
    empty-icon="pi-sitemap"
    @retry="list.refetch()"
  >
    <template #toolbar>
      <EquipmentField :label="t('equipment.search')" class="eqs__wide">
        <div class="eq-search">
          <i class="pi pi-search" />
          <input
            v-model="search"
            class="eq-input"
            type="search"
            :placeholder="t('equipment.groups.searchPlaceholder')"
          />
        </div>
      </EquipmentField>
      <EquipmentField :label="t('equipment.fields.store')">
        <EquipmentStoreSelect v-model="storeId" allow-all />
      </EquipmentField>
      <EquipmentField :label="t('equipment.fields.site')">
        <EquipmentSiteSelect v-model="siteId" :store-id="storeId" allow-all />
      </EquipmentField>
      <EquipmentField :label="t('equipment.sort')">
        <EquipmentSelect
          v-model="sort"
          :options="sortOptions"
          :search-above="0"
          icon="pi-sort-alt"
        />
      </EquipmentField>
      <button
        v-if="filtersActive"
        type="button"
        class="eq-btn eq-btn--ghost"
        @click="resetFilters"
      >
        <i class="pi pi-filter-slash" /> {{ t('equipment.reset') }}
      </button>
    </template>

    <table class="eq-table">
      <thead>
        <tr>
          <th>{{ t('equipment.fields.groupName') }}</th>
          <th>{{ t('equipment.groups.master') }}</th>
          <th>{{ t('equipment.groups.members') }}</th>
          <th>{{ t('equipment.fields.site') }}</th>
          <th v-if="isSystemAdmin" />
        </tr>
      </thead>
      <tbody>
        <tr v-for="group in rows" :key="group.id">
          <td>
            <div class="eq-cell">
              <span class="eq-chip"><i class="pi pi-sitemap" /></span>
              <strong>{{ group.name }}</strong>
            </div>
          </td>
          <td>
            <div class="eq-cell">
              <span class="eq-chip"
                ><i class="pi" :class="typeIcon(group.master.type)"
              /></span>
              <span class="eq-stack">
                <strong>{{ group.master.label }}</strong>
                <small>{{ typeLabel(group.master.type) }}</small>
              </span>
            </div>
          </td>
          <td>
            <ol v-if="group.members.length" class="grp__members">
              <li v-for="member in group.members" :key="member.id">
                {{ member.label }}
                <span class="eq-badge">
                  {{
                    member.placement === 'default'
                      ? t('equipment.groups.default')
                      : t('equipment.groups.fallback', {
                          n: member.fallbackIndex ?? 1,
                        })
                  }}
                </span>
              </li>
            </ol>
            <span v-else class="eq-muted">—</span>
          </td>
          <td>
            {{ group.site.name }}
            <div class="eq-muted">{{ group.store.name }}</div>
          </td>
          <td v-if="isSystemAdmin">
            <div class="eq-actions">
              <button
                type="button"
                class="eq-btn eq-btn--icon"
                :aria-label="t('equipment.edit')"
                @click="editing = group"
              >
                <i class="pi pi-pencil" />
              </button>
              <button
                type="button"
                class="eq-btn eq-btn--icon"
                :aria-label="t('equipment.delete')"
                @click="deleting = group"
              >
                <i class="pi pi-trash" />
              </button>
            </div>
          </td>
        </tr>
      </tbody>
    </table>
  </EquipmentTabShell>

  <GroupFormModal
    v-if="formOpen"
    :group="editing ?? undefined"
    @close="closeForm"
    @saved="closeForm"
  />
  <EquipmentConfirmModal
    v-if="deleting"
    :title="t('equipment.groups.delete')"
    :message="t('equipment.groups.deleteMessage', { name: deleting.name })"
    :run="
      () =>
        EquipmentService.deleteGroup(
          deleting!.store.id,
          deleting!.site.id,
          deleting!.id,
        )
    "
    @close="deleting = null"
    @done="onDeleted"
  />
</template>

<style scoped>
.grp__members {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
  margin: 0;
  padding: 0;
  list-style: none;
}
</style>
